import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/material/styles';
import {
    CLIENT_PROFILE_STAGE_KEYS,
    MAX_ALLOWED_AGE_FF,
    MIN_ALLOWED_AGE_FF,
    MAX_ALLOWED_RETIREMENT_AGE,
    MAX_RETIREMENT_AGE_WARNING,
    DEFAULT_RETIREMENT_AGE,
    EWS_DURATION_KEYS,
    RETIREMENT_GOAL,
    STATUSVARIANTS,
    iwsGoalTypeMapper,
    PARTICIPANT_ID_AVAILABLE_TENANTS,
    FEATURE_NAMES
} from '../../common/constants';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { addAgeToDate, hex2rgba } from '../../common/helper';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { clearClientError, createClient, saveClient } from '../../features/client';
import { BasicButton, BasicDatePicker, TextboxInput, Typography } from '../../components/atoms';
import { DrawerComp } from '../../components/molecules';
import { ReactComponent as AccountCircle } from '../../assets/icons/account_circle.svg';
import { saveGoal, syncAdviceGoals, updateGoal } from '../../features/client-goals';
import { setAffectedEwsTerms } from '../../features/ews';
import { isEqual, checkFFEnabled } from '../../utils';
import { getClientJourneyFlowType } from '../../utils';
import { splitClientName } from '../../common/helper';
import { TargetDateKeys } from '../../common/types';
// import { Add } from '@mui/icons-material';      // Commented for future purpose incase of multiple relationships

export const GridContainer: any = styled(Grid)(() => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    minHeight: '100vh'
}));

export const LeftContainer: any = styled(GridContainer)(({ theme }) => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    backgroundColor: theme.palette.layout.primary,
    alignItems: 'flex-start',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 10%'
}));

export const RightContainer: any = styled(GridContainer)(({ theme }) => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    background: `${theme.palette.layout.primary} 0.5`,
    backgroundColor: `${hex2rgba(theme.palette.layout.primary, 0.5)}`,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px'
}));

export const ErrorSpan = styled(Typography)(({ theme }) => ({
    display: 'flex',
    fontSize: '13px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '12px',
    color: theme.palette.berry[400],
    margin: 0
}));

export const WarningSpan = styled(Typography)(({ theme }) => ({
    display: 'flex',
    fontSize: '13px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '12px',
    color: theme.palette.semantic.text.warning,
    margin: 0
}));

export const ItemContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& a': {
        color: theme.palette.background.paper
    },
    gap: '0.5rem',
    marginBottom: '1rem'
}));

export const FormContainer: any = styled(GridContainer)(({ theme }) => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    backgroundColor: theme.palette.layout.white,
    padding: 30,
    borderRadius: 10,
    marginTop: '20px',
    maxWidth: '330px',
    minHeight: '75vh',
    border: '1px solid'
}));

export const Button = styled(BasicButton)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    minWidth: '35%',
    color: theme.palette.primary[500],
    textTransform: 'capitalize'
}));

export const WelcomText = styled(Typography)(({ theme }) => ({
    color: theme.palette.background.default
}));

const StyledDrawerBodyContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
}));

const StyledBasicDatePicker = styled(BasicDatePicker)(({ theme }) => ({
    margin: '24px',
    '& .MuiIconButton-root': {
        borderRadius: '50%',
        padding: '4px',
        background: theme.palette.primary[100],
        '& svg': {
            color: theme.palette.primary.dark
        }
    }
}));

interface FormInput {
    firstname: string;
    lastname: string;
    birthDate: Dayjs | null;
    // birthYear: Dayjs | null;
    relationship: string;
    retirementAge: string;
    planningAge: string;
}

export const PersonalInfoAndRelationshiopDrawerFF = ({
    isRelationship,
    openDrawer,
    setOpenDrawer,
    setActiveDrawer,
    setIsRelationship
}: any) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const theme = useTheme();
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const { loading, error, relationship } = adviceClient;
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const { orgInfo } = useAppSelector((state) => state.global);
    const RELATIONSHIP_DATA = orgInfo?.relationshipTypes?.en;
    const [relationshipinfo, setRelationshipInfo] = useState(RELATIONSHIP_DATA);
    const [relationships] = useState(adviceClient['relationship']);
    const [keyId] = useState(uuidv4());
    const { clientDob, clientFirstName } = adviceClient;
    const [selectedDay, selectedMonth, selectedYear] = clientDob?.split('-') || [];
    const [retirementAgeWarning, setRetirementAgeWarning] = useState('');
    const [planningAgeWarning, setPlanningAgeWarning] = useState('');
    // Show local error first; otherwise surface Redux backend error while drawer is open.
    const [apiErrorToken, setApiErrorToken] = useState(0);
    const [serverError, setServerError] = useState<any>('');
    const displayedServerError = openDrawer ? serverError || error : '';
    const serverErrorMessage =
        displayedServerError?.response?.data?.message === 'CLIENT_ALREADY_EXISTS'
            ? t('CLIENT_ALREADY_EXISTS_DEFAULT')
            : displayedServerError?.response?.data?.message;

    const clientDob_dayjs = clientDob
        ? dayjs()
              .set('date', parseInt(selectedDay))
              .set('month', parseInt(selectedMonth) - 1)
              .set('year', parseInt(selectedYear))
        : null;
    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
        reset,
        clearErrors,
        watch,
        trigger
    } = useForm<FormInput>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            firstname: clientFirstName || '',
            lastname: '',
            birthDate: clientDob_dayjs,
            relationship: isRelationship ? '' : 'Primary',
            retirementAge: '',
            planningAge: ''
        }
    });

    const watchedFirstName = watch('firstname');
    const watchedLastName = watch('lastname');

    useEffect(() => {
        if (watchedFirstName && watchedLastName) {
            trigger(['firstname', 'lastname']);
        }
    }, [watchedFirstName, watchedLastName, trigger]);

    // Helper function to determine if this is an existing form with data
    const hasExistingData = (data: any) => {
        return data && (data.firstName || data.lastName || data.age);
    };

    // Helper functions to get default first/last names, splitting if needed
    const getDefaultFirstName = (firstName: string, lastName: string, hasData: boolean) => {
        if (firstName && firstName.includes(' ') && !lastName && hasData) {
            const { firstName: splitFirst } = splitClientName(firstName);
            return splitFirst;
        }
        return firstName || '';
    };

    const getDefaultLastName = (firstName: string, lastName: string, hasData: boolean) => {
        if (firstName && firstName.includes(' ') && !lastName && hasData) {
            const { lastName: splitLast } = splitClientName(firstName);
            return splitLast;
        }
        return lastName || '';
    };

    // Helper function to get default values for retirement/planning age
    const getDefaultAge = (existingValue: string, hasData: boolean) => {
        if (existingValue) {
            // Strip leading zeros (e.g., 078 -> 78). Keeps single zero if the entire string was zeros.
            const str = String(existingValue).trim();
            const stripped = str.replace(/^0+/, '');
            return stripped === '' ? '0' : stripped;
        }
        if (hasData) return '65';
        return '';
    };

    const getDefaultPlanningAge = (existingValue: string, hasData: boolean) => {
        if (existingValue) {
            // Strip leading zeros (e.g., 078 -> 78). Keeps single zero if the entire string was zeros.
            const str = String(existingValue).trim();
            const stripped = str.replace(/^0+/, '');
            return stripped === '' ? '0' : stripped;
        }
        if (hasData) return '86';
        return '';
    };

    // Helper function to calculate current age based on birth date
    // eslint-disable-next-line
    const calculateCurrentAge = (birthDate: Dayjs | null): number => {
        if (!birthDate || !birthDate.isValid()) {
            return 0;
        }
        return dayjs().diff(birthDate, 'year');
    };

    // Helper function to validate retirement age and set warnings
    const validateRetirementAge = (retirementAge: string, birthDate: Dayjs | null): string | boolean => {
        if (!retirementAge || !birthDate || !birthDate.isValid()) {
            return true;
        }

        const ageNum = parseInt(retirementAge);
        if (isNaN(ageNum) || ageNum <= 0) {
            return isRelationship
                ? t('RETIREMENT_AGE_CURRENT_COCLIENT_AGE_ERROR')
                : t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR');
        }
        if (ageNum === 0) {
            setRetirementAgeWarning('');
            return isRelationship
                ? t('RETIREMENT_AGE_CURRENT_COCLIENT_AGE_ERROR')
                : t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR');
        }

        if (ageNum > MAX_ALLOWED_RETIREMENT_AGE) {
            setRetirementAgeWarning('');
            return t('RETIREMENT_AGE_GREATER_THAN_ONEEIGHTEEN_ERROR');
        }

        const currentAge = calculateCurrentAge(birthDate);

        // Error: Must be greater than or equal to current age
        if (ageNum < currentAge) {
            setRetirementAgeWarning('');
            return isRelationship
                ? t('RETIREMENT_AGE_CURRENT_COCLIENT_AGE_ERROR')
                : t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR');
        }

        // Warnings
        if (ageNum < DEFAULT_RETIREMENT_AGE) {
            setRetirementAgeWarning(
                isRelationship
                    ? t('RETIREMENT_AGE_LESS_THAN_SIXTYFIVE_WARNING_COCLIENT')
                    : t('RETIREMENT_AGE_LESS_THAN_SIXTYFIVE_WARNING')
            );
        } else if (ageNum > MAX_RETIREMENT_AGE_WARNING && ageNum <= MAX_ALLOWED_RETIREMENT_AGE) {
            setRetirementAgeWarning(
                isRelationship
                    ? t('RETIREMENT_AGE_GREATER_THAN_SEVENTYFIVE_WARNING_COCLIENT')
                    : t('RETIREMENT_AGE_GREATER_THAN_SEVENTYFIVE_WARNING')
            );
        } else {
            setRetirementAgeWarning('');
        }

        return true;
    };

    // Helper function to validate planning age and set warnings
    const validatePlanningAge = (planningAge: string, retirementAge: string): string | boolean => {
        if (!planningAge) {
            return true;
        }

        const planningAgeNum = parseInt(planningAge);
        if (isNaN(planningAgeNum)) {
            return t('PLANNING_AGE_GREATER_THAN_RETIREMENT_AGE_ERROR');
        }

        // Error: If user enters 0, show the planning age greater than retirement age error
        if (planningAgeNum === 0) {
            setPlanningAgeWarning('');
            return t('PLANNING_AGE_GREATER_THAN_RETIREMENT_AGE_ERROR');
        }

        if (planningAgeNum < 0) {
            return t('PLANNING_AGE_GREATER_THAN_RETIREMENT_AGE_ERROR');
        }

        // Error: Cannot exceed 120 years
        if (planningAgeNum > 120) {
            setPlanningAgeWarning('');
            return t('PLANNING_AGE_GREATER_THAN_ONETWENTY_ERROR');
        }

        // Error: Must be greater than retirement age
        if (retirementAge) {
            const retirementAgeNum = parseInt(retirementAge);
            if (!isNaN(retirementAgeNum) && planningAgeNum <= retirementAgeNum) {
                setPlanningAgeWarning('');
                return t('PLANNING_AGE_GREATER_THAN_RETIREMENT_AGE_ERROR');
            }
        }

        setPlanningAgeWarning('');
        return true;
    };

    // Watch form values for year calculations
    const watchedBirthDate = watch('birthDate');
    const watchedRetirementAge = watch('retirementAge');
    const watchedPlanningAge = watch('planningAge');

    // Update warnings when values change
    useEffect(() => {
        if (watchedRetirementAge && watchedBirthDate) {
            validateRetirementAge(watchedRetirementAge, watchedBirthDate);
        } else {
            setRetirementAgeWarning('');
        }
    }, [watchedRetirementAge, watchedBirthDate]);

    useEffect(() => {
        if (watchedPlanningAge) {
            validatePlanningAge(watchedPlanningAge, watchedRetirementAge);
        } else {
            setPlanningAgeWarning('');
        }
    }, [watchedPlanningAge, watchedRetirementAge]);

    const clearDrawerTransientState = () => {
        dispatch(clearClientError());
        setServerError('');
        setRetirementAgeWarning('');
        setPlanningAgeWarning('');
        clearErrors();
    };

    const setDrawerOpenState = (nextOpenState: boolean) => {
        if (!nextOpenState) {
            clearDrawerTransientState();
        }
        setOpenDrawer(nextOpenState);
    };

    // Function to calculate year based on birth date and age
    const calculateYear = (birthDate: Dayjs | null, age: string): string => {
        if (!birthDate || !age || !birthDate.isValid()) {
            return '';
        }
        const ageNum = parseInt(age);
        if (isNaN(ageNum)) {
            return '';
        }
        const calculatedYear = birthDate.year() + ageNum;
        const calculatedDate = birthDate.year(calculatedYear);
        return `${calculatedDate.format('MMMM')}, ${calculatedYear}`;
    };

    // Function to check if form is edited
    const checkIfFormIsEdited = () => {
        const formValues = watch();
        const currentFormValues = {
            firstname: formValues.firstname,
            lastname: formValues.lastname,
            birthDate: formValues.birthDate ? formValues.birthDate.format('DD-MM-YYYY') : null,
            relationship: isRelationship ? '' : 'Primary',
            retirementAge: formValues?.retirementAge,
            planningAge: formValues.planningAge
        };

        let initialFormValues;

        if (isRelationship) {
            const relationshipObj = adviceClient?.relationship?.[0];
            if (relationshipObj) {
                initialFormValues = {
                    firstname: getDefaultFirstName(
                        relationshipObj.firstName,
                        relationshipObj.lastName,
                        hasExistingData(relationshipObj)
                    ),
                    lastname: getDefaultLastName(
                        relationshipObj.firstName,
                        relationshipObj.lastName,
                        hasExistingData(relationshipObj)
                    ),
                    birthDate: relationshipObj?.age || null,
                    relationship: relationshipObj.relationship || '',
                    retirementAge: getDefaultAge(relationshipObj.retirementAge, hasExistingData(relationshipObj)),
                    planningAge: getDefaultPlanningAge(relationshipObj.planningAge, hasExistingData(relationshipObj))
                };
            } else {
                initialFormValues = {
                    firstname: '',
                    lastname: '',
                    birthDate: null,
                    relationship: '',
                    retirementAge: '',
                    planningAge: ''
                };
            }
        } else {
            const clientData = { firstName: clientFirstName, lastName: adviceClient.clientLastName, age: clientDob };
            initialFormValues = {
                firstname: getDefaultFirstName(
                    clientFirstName,
                    adviceClient.clientLastName,
                    hasExistingData(clientData)
                ),
                lastname: getDefaultLastName(clientFirstName, adviceClient.clientLastName, hasExistingData(clientData)),
                birthDate: clientDob || null,
                relationship: 'Primary',
                retirementAge: getDefaultAge(adviceClient.retirementAge, hasExistingData(clientData)),
                planningAge: getDefaultPlanningAge(adviceClient.planningAge, hasExistingData(clientData))
            };
        }

        return !isEqual(initialFormValues, currentFormValues);
    };

    useEffect(() => {
        if (isRelationship && relationship.length > 0) {
            const relationData = relationship[0];
            if (relationData.relationship == 'Partner' || relationData.relationship == 'Spouse') {
                const filteredData = relationshipinfo.filter(
                    (item: typeof RELATIONSHIP_DATA) => item.value != 'Spouse' && item.value != 'Partner'
                );
                setRelationshipInfo(filteredData);
            }
        }
    }, [relationship.length]);

    var originalObject = JSON.parse(JSON.stringify(adviceClient));
    const keysToRemove = ['loading', 'error'];
    const updatedObject = Object.assign({}, originalObject);
    keysToRemove.forEach((key) => delete updatedObject[key]);

    const onsubmitHandler = async (values: FormInput) => {
        const dob = values?.birthDate;
        const finalDate = dob?.format('DD-MM-YYYY');
        let goalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
        if (goalsData.global?.enablePDFGenerate) goalsData.global.enablePDFGenerate = false;
        // Reset the response array ref: AEX-2199
        if (goalsData.response?.length > 0) goalsData.response = [];
        // Update goalsData.formFields[].data.planStartRetirement if any goal has client's or co-client's old retirement date ref: AEX-2199
        // Update goalsData.formFields[].data.targetDate if any goal has client's or co-client's old planning date ref: AEX-2199
        if (adviceGoals?.formFields?.length > 0) {
            const newClientPlanning = isRelationship
                ? addAgeToDate(adviceClient?.clientDob, adviceClient?.planningAge)
                : addAgeToDate(finalDate, values.planningAge);
            const newClientRetirement = isRelationship
                ? addAgeToDate(adviceClient?.clientDob, adviceClient?.retirementAge)
                : addAgeToDate(finalDate, values.retirementAge);
            const newCoClientPlanning = isRelationship
                ? addAgeToDate(finalDate, values.planningAge)
                : addAgeToDate(adviceClient?.relationship?.[0]?.age, adviceClient?.relationship?.[0]?.planningAge);
            const newCoClientRetirement = isRelationship
                ? addAgeToDate(finalDate, values.retirementAge)
                : addAgeToDate(adviceClient?.relationship?.[0]?.age, adviceClient?.relationship?.[0]?.retirementAge);
            goalsData.formFields = adviceGoals.formFields.map((field) => {
                if (field.data.goalType !== RETIREMENT_GOAL && field.data.goalType !== iwsGoalTypeMapper.retirement) {
                    return field;
                }

                const planStartUpdate =
                    field.data.planStartRetirementKey === TargetDateKeys.client
                        ? newClientRetirement
                        : field.data.planStartRetirementKey === TargetDateKeys.coClient
                        ? newCoClientRetirement
                        : undefined;
                const targetDateUpdate =
                    field.data.targetDateKey === TargetDateKeys.client
                        ? newClientPlanning
                        : field.data.targetDateKey === TargetDateKeys.coClient
                        ? newCoClientPlanning
                        : undefined;

                // Only clone if updates are needed
                if (!planStartUpdate && !targetDateUpdate) {
                    return field;
                }

                return {
                    ...field,
                    data: {
                        ...field.data,
                        ...(planStartUpdate && { planStartRetirement: planStartUpdate }),
                        ...(targetDateUpdate && { targetDate: targetDateUpdate })
                    }
                };
            });
        }
        // Save goals only if there are changes
        if (JSON.stringify(goalsData) !== JSON.stringify(adviceGoals) && !isFlow2) await dispatch(saveGoal(goalsData));
        if (isRelationship) {
            const originalObject = JSON.parse(JSON.stringify(adviceClient));
            const keysToRemove = ['loading', 'error'];
            const updatedObject = Object.assign({}, originalObject);
            keysToRemove.forEach((key) => delete updatedObject[key]);
            const existingRel =
                Array.isArray(updatedObject?.relationship) && updatedObject?.relationship?.length > 0
                    ? updatedObject?.relationship[0]
                    : null;
            const payloadData = {
                id: existingRel?.id || keyId,
                firstName: values.firstname,
                lastName: values.lastname,
                age: finalDate,
                relationship: values.relationship,
                retirementAge: values.retirementAge,
                planningAge: values.planningAge
            };
            updatedObject['relationship'] = [payloadData];
            (updatedObject['stages'] = adviceClient?.stages?.includes(CLIENT_PROFILE_STAGE_KEYS[1])
                ? adviceClient?.stages
                : [...adviceClient?.stages, CLIENT_PROFILE_STAGE_KEYS[1]]),
                await dispatch(saveClient(updatedObject));
        } else {
            const payloadData = {
                clientFirstName: values.firstname,
                clientLastName: values.lastname,
                clientDob: finalDate,
                relationship: [],
                tenant: adviceClient?.tenant,
                accounts: adviceClient?.accounts,
                stages: adviceClient?.stages.includes(CLIENT_PROFILE_STAGE_KEYS[1])
                    ? adviceClient?.stages
                    : [...adviceClient?.stages, CLIENT_PROFILE_STAGE_KEYS[1]],
                riskProfile: adviceClient?.riskProfile,
                step: updatedObject.step,
                retirementAge: values.retirementAge,
                planningAge: values.planningAge,
                ...(checkFFEnabled(FEATURE_NAMES['CUSTOMER_ID_FEATURE_AEX-3360_AEX-3380']) && {
                    isParticipantIdAvailable: PARTICIPANT_ID_AVAILABLE_TENANTS.includes(adviceClient?.tenant ?? '')
                })
            };
            if (clientDob && clientFirstName) {
                const originalObject = JSON.parse(JSON.stringify(adviceClient));
                const keysToRemove = ['loading', 'error', 'hash'];
                const updatedObject = Object.assign({}, originalObject);
                keysToRemove.forEach((key) => delete updatedObject[key]);
                updatedObject['clientFirstName'] = payloadData.clientFirstName;
                updatedObject['clientLastName'] = payloadData.clientLastName;
                updatedObject['clientDob'] = payloadData.clientDob;
                updatedObject['retirementAge'] = payloadData.retirementAge;
                updatedObject['planningAge'] = payloadData.planningAge;
                await dispatch(saveClient(updatedObject));
            } else await dispatch(createClient(payloadData)).unwrap();
        }
        dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                response: [],
                formFields: goalsData.formFields,
                global: {
                    ...adviceGoals.global,
                    responseGoalCalculator: [],
                    ...(isFlow2 && { status: STATUSVARIANTS.INCOMPLETE }),
                    enablePDFGenerate: false
                }
            })
        );
    };
    const onSubmit = async (values: FormInput) => {
        try {
            if (!checkIfFormIsEdited()) {
                setDrawerOpenState(false);
                return;
            }
            await onsubmitHandler(values);
            setDrawerOpenState(false);
        } catch (e: any) {
            if (e?.response?.data?.name?.toLowerCase() === 'validationerror' && e?.response?.status === 400) {
                setServerError(e);
            } else {
                setApiErrorToken((currentToken) => currentToken + 1);
            }
        }
    };

    const secondarySubmit = async () => {
        try {
            if (checkIfFormIsEdited()) {
                const formValues = watch();
                await onsubmitHandler(formValues);
            }
            // Only proceed to co-client drawer if no error occurred
            setIsRelationship ? setIsRelationship(true) : setActiveDrawer('relationship');
        } catch (e: any) {
            if (e?.response?.data?.name?.toLowerCase() === 'validationerror' && e?.response?.status === 400) {
                setServerError(e);
            } else {
                setApiErrorToken((currentToken) => currentToken + 1);
                setServerError('');
            }
        }
    };
    const persistUnsavedRelationships = async () => {
        if (checkIfFormIsEdited()) {
            try {
                var originalObject = JSON.parse(JSON.stringify(adviceClient));
                const keysToRemove = ['loading', 'error'];
                const updatedObject = Object.assign({}, originalObject);
                keysToRemove.forEach((key) => delete updatedObject[key]);
                updatedObject['relationship'] = Array.isArray(relationships) ? [...relationships] : [];
                (updatedObject['stages'] = [...adviceClient?.stages, CLIENT_PROFILE_STAGE_KEYS[2]]),
                    await dispatch(saveClient(updatedObject));
                let goalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                if (goalsData.global?.enablePDFGenerate) {
                    goalsData.global.enablePDFGenerate = false;
                    await dispatch(updateGoal(goalsData));
                }
                reset();
            } catch (error) {
                console.error('Error in adding relationships', error);
            }
        }
    };

    const checkUnsavedAndhandleClose = async (flag: boolean) => {
        if (flag) await persistUnsavedRelationships();
        setDrawerOpenState(flag);
        setServerError('');
    };

    const resetChild = () => {
        reset(
            isRelationship
                ? { birthDate: null as Dayjs | null, relationship: '', retirementAge: '', planningAge: '' }
                : { birthDate: null as Dayjs | null, relationship: 'Primary', retirementAge: '', planningAge: '' }
        );
        clearDrawerTransientState();
    };

    const handleErrorReset = () => {
        if (displayedServerError) {
            dispatch(clearClientError());
            setServerError('');
        }
    };

    useEffect(() => {
        const coclientDateSplit = relationship[0]?.age.split('-') || [];
        const formattedCoClientDob = relationship[0]?.age
            ? dayjs()
                  .set('date', parseInt(coclientDateSplit[0]))
                  .set('month', parseInt(coclientDateSplit[1]) - 1)
                  .set('year', parseInt(coclientDateSplit[2]))
            : null;
        setRelationshipInfo(
            isRelationship
                ? RELATIONSHIP_DATA?.filter((item: typeof RELATIONSHIP_DATA) => item.value != 'Primary')
                : RELATIONSHIP_DATA
        );

        reset(
            isRelationship
                ? {
                      firstname: getDefaultFirstName(
                          adviceClient.relationship[0]?.firstName,
                          adviceClient.relationship[0]?.lastName,
                          hasExistingData(adviceClient.relationship[0])
                      ),
                      lastname: getDefaultLastName(
                          adviceClient.relationship[0]?.firstName,
                          adviceClient.relationship[0]?.lastName,
                          hasExistingData(adviceClient.relationship[0])
                      ),
                      birthDate: formattedCoClientDob,
                      //   relationship: adviceClient.relationship[0]?.relationship || '',
                      relationship: '',
                      retirementAge: getDefaultAge(
                          adviceClient.relationship[0]?.retirementAge,
                          hasExistingData(adviceClient.relationship[0])
                      ),
                      planningAge: getDefaultPlanningAge(
                          adviceClient.relationship[0]?.planningAge,
                          hasExistingData(adviceClient.relationship[0])
                      )
                  }
                : {
                      firstname: getDefaultFirstName(
                          clientFirstName,
                          adviceClient.clientLastName,
                          hasExistingData({
                              firstName: clientFirstName,
                              lastName: adviceClient.clientLastName,
                              age: clientDob
                          })
                      ),
                      lastname: getDefaultLastName(
                          clientFirstName,
                          adviceClient.clientLastName,
                          hasExistingData({
                              firstName: clientFirstName,
                              lastName: adviceClient.clientLastName,
                              age: clientDob
                          })
                      ),
                      birthDate: clientDob_dayjs,
                      relationship: 'Primary',
                      retirementAge: getDefaultAge(
                          adviceClient.retirementAge,
                          hasExistingData({
                              firstName: clientFirstName,
                              lastName: adviceClient.clientLastName,
                              age: clientDob
                          })
                      ),
                      planningAge: getDefaultPlanningAge(
                          adviceClient.planningAge,
                          hasExistingData({
                              firstName: clientFirstName,
                              lastName: adviceClient.clientLastName,
                              age: clientDob
                          })
                      )
                  }
        );
    }, [isRelationship, clientDob, clientFirstName]);

    const dataContainer = (
        <>
            <form onSubmit={handleSubmit(onSubmit)} key={keyId}>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%', marginBottom: '1.5rem' }}>
                    <Typography variant="labels.large">{t('FIRST_NAME')}</Typography>
                    <Controller
                        name="firstname"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: {
                                value: true,
                                message: t('FIRST_NAME_REQUIRED')
                            },
                            maxLength: {
                                value: 50,
                                message: t('TEXT_FULLNAME_MAXLENGTH_VALIDATION')
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: t('TEXT_FULLNAME_VALIDATION')
                            },
                            validate: (value: string) => {
                                const lastName = watch('lastname');
                                if (!value || !lastName) return true;

                                const fullName = `${value} ${lastName}`.trim().toLowerCase();

                                if (isRelationship) {
                                    const clientFull = `${clientFirstName} ${adviceClient.clientLastName}`
                                        .trim()
                                        .toLowerCase();
                                    return fullName !== clientFull || t('CLIENT_COCLIENT_VALIDATION_MESSAGE');
                                } else {
                                    const coClient = adviceClient.relationship[0];
                                    if (!coClient) return true;
                                    const coClientFull = `${coClient.firstName} ${coClient.lastName}`
                                        .trim()
                                        .toLowerCase();
                                    return fullName !== coClientFull || t('CLIENT_DUPLICATE_VALIDATION_MESSAGE');
                                }
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={(e) => {
                                    const input = e.target;
                                    const { value, selectionStart } = input;
                                    const capitalized = value
                                        .split(' ')
                                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
                                        .join(' ');

                                    if (capitalized !== value) {
                                        input.value = capitalized;
                                        input.setSelectionRange(selectionStart, selectionStart);
                                    }
                                    onChange(input.value);
                                    // Reset server error if exists
                                    if (displayedServerError && Object?.keys(displayedServerError)?.length > 0) {
                                        handleErrorReset();
                                    }
                                }}
                                value={value}
                                inputRef={ref}
                                error={!!errors.firstname}
                                type="text"
                                data-testid="First-Name"
                                fullWidth
                                autoFocus
                            />
                        )}
                    />
                    {errors.firstname ? <ErrorSpan role="alert">{errors.firstname.message}</ErrorSpan> : null}
                </ItemContainer>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%', marginBottom: '1.5rem' }}>
                    <Typography variant="labels.large">{t('LAST_NAME')}</Typography>
                    <Controller
                        name="lastname"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: {
                                value: true,
                                message: t('LAST_NAME_REQUIRED')
                            },
                            maxLength: {
                                value: 50,
                                message: t('TEXT_FULLNAME_MAXLENGTH_VALIDATION')
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: t('TEXT_FULLNAME_VALIDATION')
                            },
                            validate: (value: string) => {
                                const firstName = watch('firstname');
                                if (!value || !firstName) return true;

                                const fullName = `${firstName} ${value}`.trim().toLowerCase();

                                if (isRelationship) {
                                    const clientFull = `${clientFirstName} ${adviceClient.clientLastName}`
                                        .trim()
                                        .toLowerCase();
                                    return fullName !== clientFull || t('CLIENT_COCLIENT_VALIDATION_MESSAGE');
                                } else {
                                    const coClient = adviceClient.relationship[0];
                                    if (!coClient) return true;
                                    const coClientFull = `${coClient.firstName} ${coClient.lastName}`
                                        .trim()
                                        .toLowerCase();
                                    return fullName !== coClientFull || t('CLIENT_DUPLICATE_VALIDATION_MESSAGE');
                                }
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={(e) => {
                                    const input = e.target;
                                    const { value, selectionStart } = input;
                                    const capitalized = value
                                        .split(' ')
                                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
                                        .join(' ');

                                    if (capitalized !== value) {
                                        input.value = capitalized;
                                        input.setSelectionRange(selectionStart, selectionStart);
                                    }
                                    onChange(input.value);
                                    // Reset server error if exists
                                    if (displayedServerError && Object?.keys(displayedServerError)?.length > 0) {
                                        handleErrorReset();
                                    }
                                }}
                                value={value}
                                inputRef={ref}
                                error={!!errors.lastname}
                                type="text"
                                data-testid="Last-Name"
                                fullWidth
                            />
                        )}
                    />
                    {errors.lastname ? <ErrorSpan role="alert">{errors.lastname.message}</ErrorSpan> : null}
                </ItemContainer>
                <div className="go-horizontal-align-drawer">
                    <ItemContainer sx={{ alignItems: 'flex-start', width: '100%', marginBottom: '1.5rem' }}>
                        <Typography variant="labels.large">{t('DATE_OF_BIRTH')} *</Typography>
                        <Controller
                            name="birthDate"
                            control={control}
                            rules={{
                                required: { value: true, message: t('BIRTHDATE_REQUIRED') },
                                validate: (value) => {
                                    if (!value || !value.isValid()) {
                                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                                    }
                                    // Validate age range
                                    const minDate = dayjs().subtract(MAX_ALLOWED_AGE_FF, 'year');
                                    const maxDate = dayjs().subtract(MIN_ALLOWED_AGE_FF, 'year');
                                    if (value.isBefore(minDate) || value.isAfter(maxDate)) {
                                        return t('AGE_RANGE_ERROR_MSG', {
                                            minAge: MIN_ALLOWED_AGE_FF,
                                            maxAge: MAX_ALLOWED_AGE_FF
                                        });
                                    }
                                    return true;
                                }
                            }}
                            defaultValue={null}
                            render={({ field: { onChange, onBlur, value } }) => (
                                <StyledBasicDatePicker
                                    enabled
                                    type="single"
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    data-testid="birthDate"
                                    hasError={!!errors.birthDate}
                                    showErrorMessage={true}
                                />
                            )}
                        />
                        {errors.birthDate ? <ErrorSpan role="alert">{errors.birthDate.message}</ErrorSpan> : null}
                    </ItemContainer>
                </div>
                <div className="go-horizontal-align-drawer">
                    <ItemContainer sx={{ alignItems: 'flex-start', width: '100%', marginBottom: '1.5rem' }}>
                        <Typography variant="labels.large">{t('RETIREMENT_AGE')} *</Typography>
                        <Controller
                            name="retirementAge"
                            control={control}
                            defaultValue="65"
                            rules={{
                                required: {
                                    value: true,
                                    message: t('RETIREMENT_AGE_REQUIRED')
                                },
                                pattern: {
                                    value: /^[0-9]+$/,
                                    message: t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR')
                                },
                                validate: (value: string) => validateRetirementAge(value, watchedBirthDate)
                            }}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                                <TextboxInput
                                    onBlur={onBlur}
                                    onChange={(e) => {
                                        const input = e.target;
                                        // Only allow numbers
                                        const numericValue = input.value.replace(/[^0-9]/g, '');
                                        input.value = numericValue;
                                        onChange(numericValue);
                                        // Reset server error if exists
                                        if (displayedServerError && Object?.keys(displayedServerError)?.length > 0) {
                                            handleErrorReset();
                                        }
                                    }}
                                    value={value}
                                    inputRef={ref}
                                    error={!!errors.retirementAge}
                                    type="text"
                                    data-testid="Retirement-Age"
                                    fullWidth
                                    inputProps={{ maxLength: 3 }}
                                />
                            )}
                        />
                        {errors.retirementAge ? (
                            <ErrorSpan role="alert">{errors.retirementAge.message}</ErrorSpan>
                        ) : null}
                        {retirementAgeWarning && !errors.retirementAge && (
                            <WarningSpan role="alert">{retirementAgeWarning}</WarningSpan>
                        )}
                        {calculateYear(watchedBirthDate, watchedRetirementAge) && (
                            <Typography variant="labels.large">
                                {calculateYear(watchedBirthDate, watchedRetirementAge)}
                            </Typography>
                        )}
                    </ItemContainer>
                    <ItemContainer sx={{ alignItems: 'flex-start', width: '100%', marginBottom: '1.5rem' }}>
                        <Typography variant="labels.large">{t('PLANNING_AGE')} *</Typography>
                        <Controller
                            name="planningAge"
                            control={control}
                            defaultValue="86"
                            rules={{
                                required: {
                                    value: true,
                                    message: t('PLANNING_AGE_REQUIRED')
                                },
                                pattern: {
                                    value: /^[0-9]+$/,
                                    message: t('RETIREMENT_PLANNING_RANGE_ERROR_MSG')
                                },
                                validate: (value: string) => validatePlanningAge(value, watchedRetirementAge)
                            }}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                                <TextboxInput
                                    onBlur={onBlur}
                                    onChange={(e) => {
                                        const input = e.target;
                                        // Only allow numbers
                                        const numericValue = input.value.replace(/[^0-9]/g, '');
                                        input.value = numericValue;
                                        onChange(numericValue);
                                        // Reset server error if exists
                                        if (displayedServerError && Object?.keys(displayedServerError)?.length > 0) {
                                            handleErrorReset();
                                        }
                                    }}
                                    value={value}
                                    inputRef={ref}
                                    error={!!errors.planningAge}
                                    type="text"
                                    data-testid="Planning-Age"
                                    fullWidth
                                    inputProps={{ maxLength: 3 }}
                                />
                            )}
                        />
                        {errors.planningAge ? <ErrorSpan role="alert">{errors.planningAge.message}</ErrorSpan> : null}
                        {planningAgeWarning && !errors.planningAge && (
                            <WarningSpan role="alert">{planningAgeWarning}</WarningSpan>
                        )}
                        {calculateYear(watchedBirthDate, watchedPlanningAge) && (
                            <Typography variant="labels.large">
                                {calculateYear(watchedBirthDate, watchedPlanningAge)}
                            </Typography>
                        )}
                    </ItemContainer>
                </div>
            </form>
        </>
    );

    return (
        <>
            {openDrawer && (
                <DrawerComp
                    onSubmit={handleSubmit(onSubmit)}
                    resetChild={resetChild}
                    loading={loading}
                    error={
                        !!errors.firstname ||
                        !!errors.lastname ||
                        !!errors.retirementAge ||
                        !!errors.planningAge ||
                        !!errors.birthDate ||
                        !!errors.relationship ||
                        displayedServerError
                    }
                    errorMessage={serverErrorMessage}
                    header={isRelationship ? t('CREATE_RELATIONSHIP_HEADER_TEXT') : t('DRAWER_HEADER')}
                    drawerIcon={<AccountCircle color={theme.palette.primary[500]} fill={theme.palette.primary[100]} />}
                    open={openDrawer}
                    handleClose={checkUnsavedAndhandleClose}
                    disableSubmit={!isValid}
                    isValid={!isRelationship && !isValid}
                    hasChange={checkIfFormIsEdited()}
                    secondaryButtonLabel={
                        isRelationship ? false : relationships.length > 0 ? t('EDIT_COCLIENT') : t('ADD_COCLIENT')
                    }
                    onSecondarySubmit={secondarySubmit}
                    heapId={isRelationship ? 'coclient-drawer-submit' : 'client-drawer'}
                    APIError={apiErrorToken}
                >
                    <StyledDrawerBodyContainer>{dataContainer}</StyledDrawerBodyContainer>
                </DrawerComp>
            )}
        </>
    );
};
